import React from 'react'

import { connectDeviceSse, type DeviceSseConnection } from '@/api/mqtt.api'
import { refreshSession, handleAuthFailure } from '@/api/http.api'
import { TIMEOUT_MS } from '@/constants/devices'
import type { DeviceTelemetryBySerial } from '@/features/dashboard/types'

const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const WATCHDOG_INTERVAL_MS = 45_000
const WATCHDOG_STALE_MS = 70_000

type UseDeviceSSEProps = {
  deviceIds: (string | undefined)[]
}

const useDeviceSSE = ({ deviceIds }: UseDeviceSSEProps): DeviceTelemetryBySerial => {
  const [telemetryBySerial, setTelemetryBySerial] = React.useState<DeviceTelemetryBySerial>({})
  const staleTimersRef = React.useRef<Record<string, number>>({})
  const connectionRef = React.useRef<DeviceSseConnection | null>(null)
  const reconnectTimerRef = React.useRef<number | null>(null)
  const backoffRef = React.useRef(RECONNECT_BASE_MS)
  const lastEventAtRef = React.useRef(Date.now())
  const closedByUsRef = React.useRef(false)
  /* Klucz zależności powstaje z listy JUŻ znormalizowanej — bez pustych, bez duplikatów
     i posortowanej. Gdy normalizacja siedziała dopiero w ciele `useMemo`, a kluczem była
     surowa lista, samo przestawienie kolejności tych samych identyfikatorów dawało nowy
     klucz, nową referencję i pełne zerwanie połączenia SSE przy niezmienionym zbiorze
     urządzeń. Kolejność przychodzi z payloadu profilu, więc nie jest niczym gwarantowana. */
  const requestedIdsKey = JSON.stringify(
    Array.from(new Set(deviceIds.filter((id): id is string => Boolean(id)))).sort(),
  )
  const requestedIds = React.useMemo(() => JSON.parse(requestedIdsKey) as string[], [requestedIdsKey])

  React.useEffect(() => {
    const requestedIdsSet = new Set(requestedIds)
    let active = true

    closedByUsRef.current = false
    lastEventAtRef.current = Date.now()

    setTelemetryBySerial((previous) => {
      const remaining = Object.fromEntries(
        Object.entries(previous).filter(([serialNumber]) => requestedIdsSet.has(serialNumber)),
      )
      return Object.keys(remaining).length === Object.keys(previous).length ? previous : remaining
    })

    Object.keys(staleTimersRef.current).forEach((serialNumber) => {
      if (requestedIdsSet.has(serialNumber)) return
      window.clearTimeout(staleTimersRef.current[serialNumber])
      delete staleTimersRef.current[serialNumber]
    })

    function scheduleStale(deviceId: string, receivedAt: number, allowMissingSnapshot = false) {
      window.clearTimeout(staleTimersRef.current[deviceId])
      const remaining = Math.max(0, receivedAt + TIMEOUT_MS - Date.now())
      const timer = window.setTimeout(() => {
        setTelemetryBySerial((previous) => {
          const current = previous[deviceId]
          if (!current) {
            if (!allowMissingSnapshot) return previous
            return {
              ...previous,
              [deviceId]: { frames: [], receivedAt, freshness: 'stale' },
            }
          }
          if (current.receivedAt !== receivedAt || current.freshness === 'stale') return previous

          return { ...previous, [deviceId]: { ...current, freshness: 'stale' } }
        })
        if (staleTimersRef.current[deviceId] === timer) delete staleTimersRef.current[deviceId]
      }, remaining)
      staleTimersRef.current[deviceId] = timer
    }

    const subscribedAt = Date.now()
    requestedIds.forEach((deviceId) => {
      if (staleTimersRef.current[deviceId] === undefined) {
        scheduleStale(deviceId, subscribedAt, true)
      }
    })

    const closeConnection = () => {
      connectionRef.current?.close()
      connectionRef.current = null
    }

    const cleanupConnection = () => {
      active = false
      closedByUsRef.current = true
      closeConnection()
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    function scheduleReconnect() {
      if (!active || closedByUsRef.current || reconnectTimerRef.current !== null) return

      const delay = Math.min(backoffRef.current, RECONNECT_MAX_MS)
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS)
        refreshThenConnect()
      }, delay)
    }

    function refreshThenConnect() {
      if (!active || closedByUsRef.current) return
      refreshSession()
        .then(() => {
          if (active && !closedByUsRef.current) connect()
        })
        .catch((error: { response?: { status?: number } }) => {
          if (error?.response?.status === 401) {
            handleAuthFailure()
          } else if (active && !closedByUsRef.current) {
            connect()
          }
        })
    }

    function connect() {
      if (!active || closedByUsRef.current || requestedIds.length === 0) return

      closeConnection()
      connectionRef.current = connectDeviceSse(requestedIds, {
        onOpen: () => {
          if (!active) return
          backoffRef.current = RECONNECT_BASE_MS
          lastEventAtRef.current = Date.now()
        },
        onData: ({ deviceId, frames }) => {
          if (!active || !requestedIdsSet.has(deviceId)) return

          const now = Date.now()
          const latestFrameAt = Math.max(...frames.map((frame) => new Date(frame.createdAt).getTime()))
          const receivedAt = Number.isFinite(latestFrameAt) ? Math.min(latestFrameAt, now) : now
          const freshness = receivedAt + TIMEOUT_MS <= now ? 'stale' : 'live'
          lastEventAtRef.current = now
          setTelemetryBySerial((previous) => ({
            ...previous,
            [deviceId]: { frames, receivedAt, freshness },
          }))
          if (freshness === 'live') {
            scheduleStale(deviceId, receivedAt)
          } else {
            window.clearTimeout(staleTimersRef.current[deviceId])
            delete staleTimersRef.current[deviceId]
          }
        },
        onHeartbeat: () => {
          if (active) lastEventAtRef.current = Date.now()
        },
        onError: () => {
          if (!active || closedByUsRef.current) return
          closeConnection()
          scheduleReconnect()
        },
        onParseError: (rawData) => {
          if (!active) return
          lastEventAtRef.current = Date.now()
          console.error('Failed to parse SSE data:', rawData)
        },
      })
    }

    connect()

    const watchdog = window.setInterval(() => {
      if (!active || requestedIds.length === 0 || Date.now() - lastEventAtRef.current <= WATCHDOG_STALE_MS) return

      console.warn('SSE watchdog: no event for too long — reconnecting')
      closeConnection()
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      backoffRef.current = RECONNECT_BASE_MS
      refreshThenConnect()
    }, WATCHDOG_INTERVAL_MS)

    return () => {
      cleanupConnection()
      window.clearInterval(watchdog)
    }
  }, [requestedIds])

  React.useEffect(
    () => () => {
      Object.values(staleTimersRef.current).forEach((timer) => window.clearTimeout(timer))
      staleTimersRef.current = {}
    },
    [],
  )

  return telemetryBySerial
}

export default useDeviceSSE
